import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, FileText } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>Terms of Service | FutureThought</title>
        <meta name="description" content="FutureThought terms of service. Read the terms governing your use of our technology news and thought leadership website." />
        <link rel="canonical" href="https://futurethought.emergent.host/terms" />
      </Helmet>

      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold font-['Playfair_Display']">
              Future<span className="text-[hsl(var(--primary))]">Thought</span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <FileText size={20} className="text-[hsl(var(--primary))]" />
            <span className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))]">Legal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-['Playfair_Display'] font-bold tracking-tight mb-8" data-testid="terms-title">
            Terms of Service
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mb-12">Last updated: July 2026</p>

          <div className="prose-custom space-y-8 text-[hsl(var(--foreground))] leading-relaxed text-justify">
            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">1. Acceptance of Terms</h2>
              <p>By accessing and using FutureThought (the "Site"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, you should not use the Site. We reserve the right to modify these terms at any time, and your continued use of the Site constitutes acceptance of any changes.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">2. Description of Service</h2>
              <p>FutureThought is a technology news and thought leadership website that provides articles, analysis, and commentary on emerging technologies including artificial intelligence, semiconductors, sustainability, energy storage, and robotics. The Site also offers a newsletter subscription service and freelance consulting information.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">3. Intellectual Property</h2>
              <p className="mb-3">All content on this Site, including but not limited to text, articles, analysis, graphics, logos, and design elements, is the intellectual property of FutureThought and its author, Jit Singh, unless otherwise stated.</p>
              <p className="mb-3">You may not reproduce, distribute, modify, create derivative works from, publicly display, or exploit any content from this Site without prior written permission, except for:</p>
              <ul className="list-disc pl-6 space-y-2 text-[hsl(var(--muted-foreground))]">
                <li>Sharing articles via the provided social sharing buttons</li>
                <li>Quoting brief excerpts with proper attribution and a link back to the original article</li>
                <li>Personal, non-commercial use such as reading and bookmarking</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">4. User Conduct</h2>
              <p>When using the Site, you agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 text-[hsl(var(--muted-foreground))]">
                <li>Use the Site for any unlawful purpose or in violation of any applicable laws</li>
                <li>Attempt to gain unauthorized access to the Site's systems or data</li>
                <li>Interfere with or disrupt the Site's functionality or security</li>
                <li>Scrape, harvest, or collect content or data without authorization</li>
                <li>Submit false information through any forms on the Site</li>
                <li>Use automated tools, bots, or scripts to access the Site without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">5. Newsletter Subscription</h2>
              <p>By subscribing to our newsletter, you consent to receive periodic emails about new articles, technology insights, and related content. You may unsubscribe at any time by contacting us. We will not share your email address with third parties for marketing purposes.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">6. Third-Party Links and Content</h2>
              <p>Our articles may contain links to third-party websites, research papers, and external resources. These links are provided for reference and informational purposes only. FutureThought does not endorse, control, or assume responsibility for the content, privacy policies, or practices of any third-party websites.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">7. Advertising</h2>
              <p>The Site displays advertisements served by Google AdSense. These advertisements may be targeted based on your browsing behavior and interests. The display of advertisements does not constitute an endorsement of the advertised products or services. For more information about how advertising data is collected, please refer to our <Link to="/privacy-policy" className="text-[hsl(var(--primary))] hover:underline">Privacy Policy</Link>.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">8. Disclaimer of Warranties</h2>
              <p>The content on this Site is provided "as is" and "as available" without warranties of any kind, either express or implied. FutureThought makes no representations or warranties regarding the accuracy, completeness, or reliability of any content. The information provided is for general informational purposes and should not be construed as professional advice. Always conduct your own research and consult qualified professionals before making business or investment decisions.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">9. Limitation of Liability</h2>
              <p>To the fullest extent permitted by law, FutureThought and its author shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Site, reliance on any content, or inability to access the Site.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">10. Governing Law</h2>
              <p>These Terms of Service shall be governed by and construed in accordance with applicable laws. Any disputes arising from the use of this Site shall be resolved through appropriate legal channels.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">11. Contact</h2>
              <p>For questions about these Terms of Service, please visit our <Link to="/contact" className="text-[hsl(var(--primary))] hover:underline">Contact Page</Link>.</p>
            </section>
          </div>
        </div>
      </main>

      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="text-xl font-bold font-['Playfair_Display']">
            Future<span className="text-[hsl(var(--primary))]">Thought</span>
          </Link>
          <div className="flex gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <Link to="/privacy-policy" className="hover:text-[hsl(var(--foreground))] transition-colors">Privacy Policy</Link>
            <span className="text-[hsl(var(--foreground))]">Terms of Service</span>
            <Link to="/contact" className="hover:text-[hsl(var(--foreground))] transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
